import React, { useState } from 'react';
import API from '../services/api';
import CircularProgress from './CircularProgress';

/**
 * ProfileCard component
 *
 * Displays the logged‑in therapist's name, license type, tenure at the practice,
 * and weekly session progress. Includes an edit button which opens a modal
 * allowing the therapist to update their profile details. The modal submits
 * both top‑level profile fields (photo URL, license info, education, specialties,
 * demographics, availability, bio, interests) and user fields (DOB, hire date,
 * emergency contact). Empty strings are converted to null to avoid overwriting
 * existing values with blanks.
 */
function ProfileCard({ profile, sessionStats, goals }) {
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Compute progress toward weekly session goal
  const weekProgress = goals.weekly_session_goal > 0
    ? (sessionStats.thisWeekCount / goals.weekly_session_goal) * 100
    : 0;
  const bonusThreshold = goals.weekly_session_goal > 0 && goals.weekly_session_bonus > 0
    ? (goals.weekly_session_bonus / goals.weekly_session_goal) * 100
    : 120;

  // Compute tenure years based on hire date (fallback to profile.tenure_years if provided)
  const tenureYears = (() => {
    if (typeof profile?.tenure_years === 'number') return profile.tenure_years;
    const hire = profile?.hire_date;
    if (!hire) return null;
    const d = new Date(hire);
    if (isNaN(d)) return null;
    const now = new Date();
    let years = now.getFullYear() - d.getFullYear();
    const hasAnniversaryPassed =
      now.getMonth() > d.getMonth() ||
      (now.getMonth() === d.getMonth() && now.getDate() >= d.getDate());
    if (!hasAnniversaryPassed) years -= 1;
    return Math.max(years, 0);
  })();

  return (
    <div style={cardStyle}>
      {/* Navy header */}
      <div style={cardHeaderStyle}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>My Profile</div>
        <button onClick={() => setShowProfileModal(true)} style={editButtonStyle}>
          Edit Profile
        </button>
      </div>

      <div style={{ padding: 16 }}>
        {/* Profile photo and basic info */}
        <div style={profileInfoContainerStyle}>
          <div style={profilePhotoStyle}>
            {profile?.photo_url ? (
              <img
                src={profile.photo_url}
                alt="Profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div style={profileInitialsStyle}>
                {profile
                  ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`
                  : ''}
              </div>
            )}
          </div>
          <div style={{ marginLeft: 16 }}>
            <h3 style={profileNameStyle}>
              {profile ? `${profile.first_name} ${profile.last_name}` : ''}
            </h3>
            {profile && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 4, gap: 8 }}>
                  {profile.license_type && (
                    <span style={licenseBadgeStyle}>{profile.license_type}</span>
                  )}
                  {typeof tenureYears === 'number' && (
                    <span style={{ fontSize: 14, color: '#374151' }}>
                      • {tenureYears === 0 ? 'First year at practice' : `${tenureYears} yr${tenureYears === 1 ? '' : 's'} at practice`}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                  <div
                    style={{
                      ...statusDotStyle,
                      backgroundColor: profile.accepting_clients ? '#10B981' : '#EF4444',
                    }}
                  />
                  <span style={{ marginLeft: 8, fontSize: 14, fontWeight: 500 }}>
                    {profile.accepting_clients ? 'Accepting clients' : 'Not accepting clients'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

      {/* Summary widgets */}
      <div style={{ ...improvedSessionGridStyle, marginTop: 16 }}>
        <div style={sessionBoxStyle}>
          <h4 style={sessionLabelStyle}>This Week</h4>
          <div style={sessionContentStyle}>
            <div>
              <div style={sessionCountStyle}>
                {sessionStats.thisWeekCount} / {goals.weekly_session_goal}
              </div>
              <div style={sessionSubtextStyle}>Sessions</div>
              {weekProgress > 100 && (
                <div
                  style={{
                    fontSize: '0.85rem',
                    color: weekProgress >= bonusThreshold ? '#7C3AED' : '#10B981',
                    fontWeight: 600,
                    marginTop: 4,
                  }}
                >
                  {weekProgress >= bonusThreshold ? 'Bonus achieved!' : 'Goal reached!'}
                </div>
              )}
            </div>
            <div style={progressCircleContainerStyle}>
              <CircularProgress
                percentage={weekProgress}
                size={90}
                strokeWidth={12}
                regularColor="#3B82F6"
                approachingColor="#10B981"
                exceedingColor="#F6AE3B"
                bonusThreshold={bonusThreshold}
                showAnimation
              />
            </div>
          </div>
        </div>
        <div style={sessionBoxStyle}>
          <h4 style={sessionLabelStyle}>Last Week</h4>
          <div style={sessionContentStyle}>
            <div>
              <div style={sessionCountStyle}>{sessionStats.lastWeekCount}</div>
              <div style={sessionSubtextStyle}>Sessions</div>
            </div>
            <div style={goalContainerStyle}>
              <div style={goalTextStyle}>Goal: {goals.weekly_session_goal}</div>
              <div style={goalTextStyle}>Bonus: {goals.weekly_session_bonus}</div>
              {sessionStats.lastWeekCount >= goals.weekly_session_bonus && (
                <div style={{ fontSize: 24, marginTop: 4 }}>🎉</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showProfileModal && profile && (
        <ProfileEditModal profile={profile} onClose={() => setShowProfileModal(false)} />
      )}
      </div>
    </div>
  );
}

/**
 * Modal for editing profile details
 *
 * Accepts the current profile and closes itself via the onClose callback. It
 * maintains local form state and posts updates to /therapist/profile. Contact
 * fields (phone, phone_extension) are now persisted. Address fields remain
 * display-only for reference. Empty strings are normalized to null, and dates
 * are converted to ISO format. On success, the modal briefly shows a success
 * message before closing itself.
 */
function ProfileEditModal({ profile, onClose }) {
  const [formData, setFormData] = useState({
    photoUrl: profile.photo_url || '',
    licenseNumber: profile.license_number || '',
    licenseType: profile.license_type || '',
    education: profile.education || '',
    clientDemographics: profile.client_demographics || '',
    acceptingClients: profile.accepting_clients !== undefined ? !!profile.accepting_clients : true,
    unavailableReason: profile.unavailable_reason || '',
    bio: profile.bio || '',
    interests: profile.interests || '',
    specialties: profile.specialties || '',
    // Contact fields (now saved to backend)
    phone: profile.phone || '',
    phone_extension: profile.phone_extension || '',
    // Address fields (display-only, not saved)
    address_line1: profile.address_line1 || '',
    address_line2: profile.address_line2 || '',
    city: profile.city || '',
    state: profile.state || '',
    zip: profile.zip || '',
    // Users table fields (saved)
    dob: profile.dob ? String(profile.dob).slice(0, 10) : '',
    hire_date: profile.hire_date ? String(profile.hire_date).slice(0, 10) : '',
    emergency_contact_name: profile.emergency_contact_name || '',
    emergency_contact_phone: profile.emergency_contact_phone || '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Handle form input changes
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Normalize a value: convert empty strings to null, trim whitespace
  const nz = (v) => {
    if (v === undefined || v === null) return null;
    if (typeof v === 'string') {
      const t = v.trim();
      return t === '' ? null : t;
    }
    return v;
  };

  // Convert various date inputs to ISO format or null
  const toISODate = (v) => {
    if (!v) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; // already YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
      const [mm, dd, yyyy] = v.split('/');
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  };

  // Submit the form data to the backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      // Build payload with camelCase keys expected by backend
      const payload = {
        photoUrl: nz(formData.photoUrl),
        licenseNumber: nz(formData.licenseNumber),
        licenseType: nz(formData.licenseType),
        education: nz(formData.education),
        clientDemographics: nz(formData.clientDemographics),
        acceptingClients: formData.acceptingClients !== undefined ? !!formData.acceptingClients : undefined,
        unavailableReason: nz(formData.unavailableReason),
        bio: nz(formData.bio),
        interests: nz(formData.interests),
        specialties: nz(formData.specialties),
        // User fields
        phone: nz(formData.phone),
        phoneExtension: nz(formData.phone_extension),
        dob: toISODate(formData.dob),
        hireDate: toISODate(formData.hire_date),
        emergencyContactName: nz(formData.emergency_contact_name),
        emergencyContactPhone: nz(formData.emergency_contact_phone),
      };
      await API.put('/therapist/profile', payload);
      setSaveSuccess(true);
      // Close modal after brief delay
      setTimeout(() => onClose(), 800);
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data || err?.message;
      console.error('PUT /therapist/profile failed:', serverMsg);
      setSaveError(`Error saving profile: ${String(serverMsg)}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <h2 style={{ marginBottom: '1.25rem' }}>Edit Your Profile</h2>
        <form onSubmit={handleSubmit}>
          {/* Profile fields */}
          <div style={formGridStyle}>
            <div style={formColumnStyle}>
              <Field label="Profile Photo URL">
                <input
                  type="text"
                  name="photoUrl"
                  value={formData.photoUrl}
                  onChange={onChange}
                  style={inputStyle}
                />
              </Field>
              <Field label="License Number">
                <input
                  type="text"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={onChange}
                  style={inputStyle}
                />
              </Field>
              <Field label="License Type">
                <input
                  type="text"
                  name="licenseType"
                  value={formData.licenseType}
                  onChange={onChange}
                  style={inputStyle}
                />
              </Field>
              <Field label="Education">
                <textarea
                  name="education"
                  value={formData.education}
                  onChange={onChange}
                  style={{ ...inputStyle, minHeight: 80 }}
                />
              </Field>
            </div>
            <div style={formColumnStyle}>
              <Field label="Specialties">
                <textarea
                  name="specialties"
                  value={formData.specialties}
                  onChange={onChange}
                  style={{ ...inputStyle, minHeight: 80 }}
                />
              </Field>
              <Field label="Client Demographics">
                <textarea
                  name="clientDemographics"
                  value={formData.clientDemographics}
                  onChange={onChange}
                  style={{ ...inputStyle, minHeight: 80 }}
                />
              </Field>
              <Field label="Bio">
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={onChange}
                  style={{ ...inputStyle, minHeight: 120 }}
                />
              </Field>
              <Field label="Personal Interests">
                <textarea
                  name="interests"
                  value={formData.interests}
                  onChange={onChange}
                  style={{ ...inputStyle, minHeight: 80 }}
                />
              </Field>
              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  <input
                    type="checkbox"
                    name="acceptingClients"
                    checked={!!formData.acceptingClients}
                    onChange={onChange}
                    style={{ marginRight: 8 }}
                  />
                  Currently accepting new clients
                </label>
              </div>
              {!formData.acceptingClients && (
                <Field label="Reason not accepting new clients">
                  <textarea
                    name="unavailableReason"
                    value={formData.unavailableReason}
                    onChange={onChange}
                    style={{ ...inputStyle, minHeight: 80 }}
                  />
                </Field>
              )}
            </div>
          </div>
          <hr style={{ margin: '1.25rem 0' }} />
          {/* Users table fields */}
          <h3 style={{ marginBottom: '0.75rem' }}>Personal & Emergency Info</h3>
          <div style={formGridStyle}>
            <Field label="Date of Birth">
              <input
                type="date"
                name="dob"
                value={formData.dob || ''}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
            <Field label="Hire Date">
              <input
                type="date"
                name="hire_date"
                value={formData.hire_date || ''}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
            <Field label="Emergency Contact Name">
              <input
                type="text"
                name="emergency_contact_name"
                value={formData.emergency_contact_name}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
            <Field label="Emergency Contact Phone">
              <input
                type="text"
                name="emergency_contact_phone"
                value={formData.emergency_contact_phone}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
            {/* Contact fields (now saved) */}
            <Field label="Phone">
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={onChange}
                style={inputStyle}
                placeholder="e.g., (555) 123-4567"
              />
            </Field>
            <Field label="Extension (optional)">
              <input
                type="text"
                name="phone_extension"
                value={formData.phone_extension}
                onChange={onChange}
                style={inputStyle}
                placeholder="e.g., 123"
              />
            </Field>
            {/* Address fields (display-only, not saved) */}
            <Field label="Address Line 1">
              <input
                type="text"
                name="address_line1"
                value={formData.address_line1}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
            <Field label="Address Line 2">
              <input
                type="text"
                name="address_line2"
                value={formData.address_line2}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
            <Field label="City">
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
            <Field label="State">
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
            <Field label="ZIP">
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={onChange}
                style={inputStyle}
              />
            </Field>
          </div>
          {saveError && <div style={errorMessageStyle}>{saveError}</div>}
          {saveSuccess && <div style={successMessageStyle}>Profile updated</div>}
          <div style={buttonContainerStyle}>
            <button
              type="button"
              onClick={onClose}
              style={cancelButtonStyle}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button type="submit" style={saveButtonStyle} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Field wrapper component for form labels and inputs
 */
function Field({ label, children }) {
  return (
    <div style={formGroupStyle}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

/* ----- Styles ----- */
const cardStyle = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 1px 8px rgba(0,0,0,0.09)',
  overflow: 'hidden',
};

const cardHeaderStyle = {
  background: '#1D2C5F',
  padding: '14px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const profileInfoContainerStyle = {
  display: 'flex',
  alignItems: 'center',
};

const profilePhotoStyle = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  overflow: 'hidden',
  backgroundColor: '#F3F4F6',
};

const profileInitialsStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  color: '#6B7280',
};

const profileNameStyle = { margin: 0 };

const licenseBadgeStyle = {
  backgroundColor: '#fdf4ef',
  color: '#C67A52',
  borderRadius: 6,
  padding: '4px 8px',
  fontWeight: 600,
  fontSize: 12,
};

const statusDotStyle = {
  width: 10,
  height: 10,
  borderRadius: '50%',
};

const improvedSessionGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
};

const sessionBoxStyle = {
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  padding: 16,
  background: 'white',
};

const sessionLabelStyle = { margin: 0, marginBottom: 6 };

const sessionContentStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const progressCircleContainerStyle = { width: 100, height: 100 };

const sessionCountStyle = { fontSize: 20, fontWeight: 700 };

const sessionSubtextStyle = { fontSize: 12, color: '#6B7280' };

const goalContainerStyle = { fontSize: 12, color: '#6B7280' };

const goalTextStyle = { fontSize: 12, color: '#6B7280' };

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  background: 'white',
  borderRadius: 12,
  padding: 20,
  width: 'min(900px, 96vw)',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
};

const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};

const formColumnStyle = { display: 'flex', flexDirection: 'column', gap: 12 };

const formGroupStyle = { display: 'flex', flexDirection: 'column' };

const labelStyle = { fontSize: '0.9rem', marginBottom: 6, color: '#374151' };

const inputStyle = {
  border: '1px solid #E5E7EB',
  borderRadius: 8,
  padding: 8,
};

const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 16,
  gap: 8,
};

const saveButtonStyle = {
  background: '#1D2C5F',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  cursor: 'pointer',
};

const cancelButtonStyle = {
  background: '#E5E7EB',
  color: '#111827',
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  cursor: 'pointer',
};

const editButtonStyle = {
  background: 'rgba(255,255,255,0.15)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.3)',
  borderRadius: 6,
  padding: '5px 13px',
  fontSize: 12,
  cursor: 'pointer',
  fontWeight: 600,
};

const errorMessageStyle = {
  backgroundColor: '#f8d7da',
  color: '#721c24',
  padding: '0.75rem',
  borderRadius: 4,
  marginBottom: '1rem',
};

const successMessageStyle = {
  backgroundColor: '#d4edda',
  color: '#155724',
  padding: '0.75rem',
  borderRadius: 4,
  marginBottom: '1rem',
};

export default ProfileCard;